import { z } from 'zod';
import omit from 'lodash/omit';
import {
  emailSchema,
  getDependentFieldOptionalSchema,
  getDependentFieldSchema,
  internationalAddressDefaultValues,
  internationalAddressOptionalSchema,
  internationalAddressSchema,
  USAAddressOptionalSchema,
  USAAddressSchema,
} from '../../../constants/sharedSchemas';
import { API_FIELDS_STUDENT_FAMILY as STUDENT_FAMILY, API_FIELDS_CONTACT as CONTACT, CONTACT_CLASSIFICATION } from '@enroller/admissions-components';
import { PUT_API_MODULE_NAMES } from '@enroller/utils';
import { PageResponseData } from '../queries/graphqlResponse';
import {
  StudentFamilyPayload,
  StudentFamilyContactsRercordFields,
  ParentsPayload,
  FamilyMemberPayload,
  FriendsInUSAPayload,
} from '../queries/pages/studentFamily';
import { StudentApplicationValues } from '../StudentApplication';

export enum STUDENT_FAMILY_SECTIONS {
  parentsSection = 'parentsSection',
  familyMembers = 'familyMembers',
  friendsInUSA = 'friendsInUSA',
}

export enum STUDENT_FAMILY_SUB_SECTIONS {
  address = 'address',
  parents = 'parents',
}

export const familySchema = {
  required: z.object({
    [STUDENT_FAMILY_SECTIONS.parentsSection]: z.object({
      [STUDENT_FAMILY_SUB_SECTIONS.parents]: z.array(
        // Parents
        z.object({
          [CONTACT.ID]: z.string().optional(),
          [CONTACT.lastName]: z.string().min(1, 'Family Name is required'),
          [CONTACT.firstName]: z.string().min(1, 'First Name is required'),
          [CONTACT.gender]: z.string().min(1, 'Gender is required'),
          [CONTACT.isLegalGuardian]: z.string().min(1, 'This field is required'),
          [STUDENT_FAMILY_SUB_SECTIONS.address]: internationalAddressSchema,
          [CONTACT.occupation]: z.string().min(1, 'Occupation is required'),
          [CONTACT.mobile]: z.string().min(1, 'Cell Phone is required'),
          [CONTACT.workPhone]: z.string().min(1, 'Work Phone is required'),
          [CONTACT.email]: emailSchema,
          [CONTACT.parentStatus]: z.string().min(1, 'This field is required'),
        }),
      ),
      [STUDENT_FAMILY.maritalStatus]: getDependentFieldSchema('maritalStatusOther', 'other'),
      [STUDENT_FAMILY.houseType]: z.array(z.string()).min(1, 'House type is required'),
    }),
    [STUDENT_FAMILY_SECTIONS.familyMembers]: z.array(
      // Parents
      z.object({
        [CONTACT.ID]: z.string().optional(),
        [CONTACT.lastName]: z.string().min(1, 'Last Name is required'),
        [CONTACT.firstName]: z.string().min(1, 'First Name is required'),
        [CONTACT.relationship]: z.string().min(1, 'Relationship is required'),
        [CONTACT.age]: z.string().min(1, 'Age is required'),
        [CONTACT.gender]: z.string().min(1, 'Gender is required'),
      }),
    ),
    [STUDENT_FAMILY_SECTIONS.friendsInUSA]: z.array(
      // Parents
      z.object({
        [CONTACT.ID]: z.string().optional(),
        [CONTACT.firstName]: z.string().min(1, 'First Name is required'),
        [CONTACT.lastName]: z.string().min(1, 'Last Name is required'),
        [CONTACT.relationship]: z.string().min(1, 'Relationship is required'),
        [CONTACT.email]: emailSchema,
        [CONTACT.phone]: z.string().min(1, 'Phone Number is required'),
        [STUDENT_FAMILY_SUB_SECTIONS.address]: USAAddressSchema,
      }),
    ),
  }),
  optional: z.object({
    [STUDENT_FAMILY_SECTIONS.parentsSection]: z.object({
      [STUDENT_FAMILY_SUB_SECTIONS.parents]: z
        .array(
          z.object({
            [CONTACT.ID]: z.string().optional(),
            [CONTACT.lastName]: z.string().optional(),
            [CONTACT.firstName]: z.string().optional(),
            [CONTACT.gender]: z.string().optional(),
            [CONTACT.isLegalGuardian]: z.string().optional(),
            [STUDENT_FAMILY_SUB_SECTIONS.address]: internationalAddressOptionalSchema,
            [CONTACT.occupation]: z.string().optional(),
            [CONTACT.mobile]: z.string().optional(),
            [CONTACT.workPhone]: z.string().optional(),
            [CONTACT.email]: z.string().email('Email is invalid').optional(),
            [CONTACT.parentStatus]: z.string().optional(),
          }),
        )
        .length(2),
      [STUDENT_FAMILY.maritalStatus]: getDependentFieldOptionalSchema('maritalStatusOther'),
      [STUDENT_FAMILY.houseType]: z.array(z.string()).optional(),
    }),
    [STUDENT_FAMILY_SECTIONS.familyMembers]: z.array(
      // Family members
      z.object({
        [CONTACT.ID]: z.string().optional(),
        [CONTACT.lastName]: z.string().optional(),
        [CONTACT.firstName]: z.string().optional(),
        [CONTACT.relationship]: z.string().optional(),
        [CONTACT.age]: z.string().optional(),
        [CONTACT.gender]: z.string().optional(),
      }),
    ),
    [STUDENT_FAMILY_SECTIONS.friendsInUSA]: z.array(
      // Friends in USA
      z.object({
        [CONTACT.ID]: z.string().optional(),
        [CONTACT.firstName]: z.string().optional(),
        [CONTACT.lastName]: z.string().optional(),
        [CONTACT.relationship]: z.string().optional(),
        [CONTACT.email]: z.string().email('Email is invalid').optional(),
        [CONTACT.phone]: z.string().optional(),
        [STUDENT_FAMILY_SUB_SECTIONS.address]: USAAddressOptionalSchema,
      }),
    ),
  }),
};

export type FamilyFormValues = z.infer<(typeof familySchema)['required']> | z.infer<(typeof familySchema)['optional']>;
export type ParentType = FamilyFormValues['parentsSection']['parents'][number];
export type FamilyMemberType = FamilyFormValues['familyMembers'][number];
export type FriendType = FamilyFormValues['friendsInUSA'][number];

const parentDefaultValues: ParentType = {
  [CONTACT.lastName]: '',
  [CONTACT.firstName]: '',
  [CONTACT.gender]: '',
  [CONTACT.isLegalGuardian]: 'no',
  [STUDENT_FAMILY_SUB_SECTIONS.address]: internationalAddressDefaultValues,
  [CONTACT.occupation]: '',
  [CONTACT.mobile]: '',
  [CONTACT.workPhone]: '',
  [CONTACT.email]: undefined,
  [CONTACT.parentStatus]: '',
};

export const familyMemberDefaultValues: FamilyMemberType = {
  [CONTACT.lastName]: '',
  [CONTACT.firstName]: '',
  [CONTACT.relationship]: '',
  [CONTACT.age]: undefined,
  [CONTACT.gender]: '',
};

export const friendDefaultValues: FriendType = {
  [CONTACT.firstName]: '',
  [CONTACT.lastName]: '',
  [CONTACT.relationship]: '',
  [CONTACT.email]: undefined,
  [CONTACT.phone]: '',
  [STUDENT_FAMILY_SUB_SECTIONS.address]: internationalAddressDefaultValues,
};

export const defaultValues: FamilyFormValues = {
  [STUDENT_FAMILY_SECTIONS.parentsSection]: {
    [STUDENT_FAMILY_SUB_SECTIONS.parents]: [parentDefaultValues, parentDefaultValues],
    [STUDENT_FAMILY.maritalStatus]: { value: '', maritalStatusOther: '' },
    [STUDENT_FAMILY.houseType]: [],
  },
  [STUDENT_FAMILY_SECTIONS.familyMembers]: [familyMemberDefaultValues],
  [STUDENT_FAMILY_SECTIONS.friendsInUSA]: [friendDefaultValues],
};

export const createStudentFamilyPayload = (data: FamilyFormValues, modulesID: StudentApplicationValues['modulesID']) => {
  const {
    [STUDENT_FAMILY_SECTIONS.parentsSection]: newParentsSection,
    [STUDENT_FAMILY_SECTIONS.friendsInUSA]: newFriendsInUSA,
    [STUDENT_FAMILY_SECTIONS.familyMembers]: newFamilyMembers,
  } = data;

  const handleContactCreation = (contact: FriendsInUSAPayload | FamilyMemberPayload) => {
    if (contact[CONTACT.ID]) {
      return contact;
    }

    return {
      ...contact,
      [CONTACT.studentFamilyLookup]: modulesID.Accounts.family,
    };
  };

  const newParentsValues: ParentsPayload[] = newParentsSection[STUDENT_FAMILY_SUB_SECTIONS.parents].map((parent) => {
    let parsedParent = {
      ...parent,
      [CONTACT.contactClassification]: CONTACT_CLASSIFICATION.guardian,
      [CONTACT.isLegalGuardian]: parent[CONTACT.isLegalGuardian] === 'yes',
    };

    if (parent[STUDENT_FAMILY_SUB_SECTIONS.address]?.street) {
      parsedParent = {
        ...parsedParent,
        ...parent[STUDENT_FAMILY_SUB_SECTIONS.address],
      };
    }

    return omit(parsedParent, STUDENT_FAMILY_SUB_SECTIONS.address);
  });

  const friendsInUSAPayload: FriendsInUSAPayload[] = newFriendsInUSA.map((friend) => {
    let parsedFriends = {
      ...friend,
      [CONTACT.contactClassification]: CONTACT_CLASSIFICATION.friendsAndRelatives,
    };

    if (friend[STUDENT_FAMILY_SUB_SECTIONS.address]?.street) {
      parsedFriends = {
        ...parsedFriends,
        ...friend[STUDENT_FAMILY_SUB_SECTIONS.address],
      };
    }

    parsedFriends = omit(parsedFriends, STUDENT_FAMILY_SUB_SECTIONS.address);
    return handleContactCreation(parsedFriends);
  });

  const familyMembersPayload: FamilyMemberPayload[] = newFamilyMembers.map((contact) =>
    handleContactCreation({
      ...contact,
      [CONTACT.contactClassification]: CONTACT_CLASSIFICATION.familyMember,
    }),
  );

  const newContactsValues = [...newParentsValues, ...friendsInUSAPayload, ...familyMembersPayload];

  const payload: StudentFamilyPayload = {
    data: {
      [PUT_API_MODULE_NAMES.ACCOUNTS]: [
        {
          [STUDENT_FAMILY.ID]: modulesID.Accounts.family,
          [STUDENT_FAMILY.maritalStatus]: (newParentsSection[STUDENT_FAMILY.maritalStatus].value as string) || '',
          [STUDENT_FAMILY.otherMaritalStatus]: (newParentsSection[STUDENT_FAMILY.maritalStatus].maritalStatusOther as string) || '',
          [STUDENT_FAMILY.houseType]: newParentsSection[STUDENT_FAMILY.houseType] || [],
        },
      ],
      [PUT_API_MODULE_NAMES.CONTACTS]: newContactsValues,
    },
  };

  return payload;
};

export const createStudentFamilyDetailsResponse = (familyResponse: PageResponseData['family']): FamilyFormValues => {
  if (!familyResponse || !Array.isArray(familyResponse?.contacts) || !familyResponse?.data) {
    console.warn('Student Family: Data not found');

    return defaultValues;
  }
  const { data: familyData, contacts: contactsData } = familyResponse;

  const reducedContacts = contactsData
    ?.filter((contact) => contact[CONTACT.relationship]?.value !== CONTACT_CLASSIFICATION.student)
    .reduce<{
      [STUDENT_FAMILY_SUB_SECTIONS.parents]: ParentType[];
      [STUDENT_FAMILY_SECTIONS.friendsInUSA]: FamilyMemberType[];
      [STUDENT_FAMILY_SECTIONS.familyMembers]: FriendType[];
    }>(
      (dataObj, contact: StudentFamilyContactsRercordFields) => {
        switch (contact[CONTACT.contactClassification]?.value) {
          case CONTACT_CLASSIFICATION.guardian:
            return {
              ...dataObj,
              [STUDENT_FAMILY_SUB_SECTIONS.parents]: [
                ...dataObj[STUDENT_FAMILY_SUB_SECTIONS.parents],
                {
                  [CONTACT.ID]: contact[CONTACT.ID]?.value || '',
                  [CONTACT.lastName]: contact[CONTACT.lastName]?.value || '',
                  [CONTACT.firstName]: contact[CONTACT.firstName]?.value || '',
                  [CONTACT.gender]: contact[CONTACT.gender]?.value || '',
                  [CONTACT.isLegalGuardian]: contact[CONTACT.isLegalGuardian]?.value ? 'yes' : 'no',
                  [STUDENT_FAMILY_SUB_SECTIONS.address]: {
                    [CONTACT.addressLine1]: contact?.[CONTACT.addressLine1]?.value || '',
                    [CONTACT.addressLine2]: contact?.[CONTACT.addressLine2]?.value || '',
                    [CONTACT.city]: contact?.[CONTACT.city]?.value || '',
                    [CONTACT.postalCode]: contact?.[CONTACT.postalCode]?.value || '',
                    [CONTACT.country]: contact?.[CONTACT.country]?.value || '',
                  },
                  [CONTACT.occupation]: contact[CONTACT.occupation]?.value || '',
                  [CONTACT.mobile]: contact[CONTACT.mobile]?.value || '',
                  [CONTACT.workPhone]: contact[CONTACT.workPhone]?.value || '',
                  [CONTACT.email]: contact[CONTACT.email]?.value || '',
                  [CONTACT.parentStatus]: contact[CONTACT.parentStatus]?.value || '',
                },
              ],
            };
          case CONTACT_CLASSIFICATION.familyMember:
            return {
              ...dataObj,
              [STUDENT_FAMILY_SECTIONS.familyMembers]: [
                ...dataObj[STUDENT_FAMILY_SECTIONS.familyMembers],
                {
                  [CONTACT.ID]: contact[CONTACT.ID]?.value || '',
                  [CONTACT.lastName]: contact[CONTACT.lastName]?.value || '',
                  [CONTACT.firstName]: contact[CONTACT.firstName]?.value || '',
                  [CONTACT.relationship]: contact[CONTACT.relationship]?.value || '',
                  [CONTACT.age]: `${contact[CONTACT.age]?.value || ''}`,
                  [CONTACT.gender]: contact[CONTACT.gender]?.value || '',
                },
              ],
            };
          case CONTACT_CLASSIFICATION.friendsAndRelatives:
            return {
              ...dataObj,
              [STUDENT_FAMILY_SECTIONS.friendsInUSA]: [
                ...dataObj[STUDENT_FAMILY_SECTIONS.friendsInUSA],
                {
                  [CONTACT.ID]: contact[CONTACT.ID]?.value || '',
                  [CONTACT.lastName]: contact[CONTACT.lastName]?.value || '',
                  [CONTACT.firstName]: contact[CONTACT.firstName]?.value || '',
                  [CONTACT.relationship]: contact[CONTACT.relationship]?.value || '',
                  [CONTACT.phone]: contact[CONTACT.phone]?.value || '',
                  [CONTACT.gender]: contact[CONTACT.gender]?.value || '',
                  [CONTACT.email]: contact[CONTACT.email]?.value || '',
                  [STUDENT_FAMILY_SUB_SECTIONS.address]: {
                    [CONTACT.addressLine1]: contact?.[CONTACT.addressLine1]?.value || '',
                    [CONTACT.addressLine2]: contact?.[CONTACT.addressLine2]?.value || '',
                    [CONTACT.city]: contact?.[CONTACT.city]?.value || '',
                    [CONTACT.postalCode]: contact?.[CONTACT.postalCode]?.value || '',
                    [CONTACT.state]: contact?.[CONTACT.state]?.value || '',
                  },
                },
              ],
            };
          default:
            return dataObj;
        }
      },
      {
        [STUDENT_FAMILY_SUB_SECTIONS.parents]: [],
        [STUDENT_FAMILY_SECTIONS.friendsInUSA]: [],
        [STUDENT_FAMILY_SECTIONS.familyMembers]: [],
      },
    );

  return {
    [STUDENT_FAMILY_SECTIONS.parentsSection]: {
      [STUDENT_FAMILY_SUB_SECTIONS.parents]: reducedContacts[STUDENT_FAMILY_SUB_SECTIONS.parents],
      [STUDENT_FAMILY.maritalStatus]: {
        value: familyData?.[STUDENT_FAMILY.maritalStatus]?.value || '',
        maritalStatusOther: familyData?.[STUDENT_FAMILY.otherMaritalStatus]?.value || '',
      },
      [STUDENT_FAMILY.houseType]: familyData?.[STUDENT_FAMILY.houseType]?.value || [],
    },
    [STUDENT_FAMILY_SECTIONS.familyMembers]: reducedContacts[STUDENT_FAMILY_SECTIONS.familyMembers],
    [STUDENT_FAMILY_SECTIONS.friendsInUSA]: reducedContacts[STUDENT_FAMILY_SECTIONS.friendsInUSA],
  };
};
